import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { formatCurrency, isoDate } from "@/lib/format";
import { calculateReport, loadConfig } from "@/lib/calcEngine";
import { Pencil, Trash2, Plus, History as HistoryIcon, Calculator, Save, X } from "lucide-react";

const CATS = ["balvatika", "primary", "upper_primary", "high_school"];
const CAT_LABEL = { balvatika: "Balvatika", primary: "Primary (I-V)", upper_primary: "Upper Primary (VI-VIII)", high_school: "High School (IX-X)" };
const CAT_EMOJI = { balvatika: "🧒", primary: "🧁", upper_primary: "📘", high_school: "🎓" };
const CAT_HEAD = {
  balvatika: "from-thali-green to-thali-dark",
  primary: "from-thali-saffron to-thali-turmericdark",
  upper_primary: "from-sky-600 to-sky-800",
  high_school: "from-rose-700 to-rose-900",
};
const UNIT_OPTIONS = ["g", "ml", "no", "unit", "kg", "l"];

export default function AdminRates() {
  const [periods, setPeriods] = useState(null);
  const [edit, setEdit] = useState(null);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [preview, setPreview] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [recipeCost, setRecipeCost] = useState(3.5);
  const [savingRecipe, setSavingRecipe] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/rates");
    setPeriods(data.periods);
    const { data: a } = await api.get("/admin/rates/audit");
    setAudit(a.logs || []);
    const { data: lr } = await api.get("/admin/laddoo-recipe");
    setRecipe(lr.ingredients);
    setRecipeCost(lr.cost);
  };
  useEffect(() => { load(); loadConfig(); }, []);

  const openEdit = (p) => setEdit(JSON.parse(JSON.stringify(p)));

  const save = async () => {
    const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
    const ratesOut = {};
    for (const cat of CATS) {
      const r = edit.rates[cat];
      const items = {};
      for (const [item, it] of Object.entries(r.items)) {
        items[item] = { ...it, rate_per_student: num(it.rate_per_student), qty_per_student: num(it.qty_per_student) };
      }
      const tt = r.target_total;
      ratesOut[cat] = { ...r, items, target_total: (tt === null || tt === "" || tt === undefined) ? null : num(tt) };
    }
    const payload = { effective_from: edit.effective_from.split("T")[0], effective_to: (edit.effective_to || "").split("T")[0], name: edit.name || "", circular: edit.circular || "", rates: ratesOut };
    try {
      if (edit.id) await api.put(`/admin/rates/${edit.id}`, payload);
      else await api.post("/admin/rates", payload);
      toast.success("Rate period saved. Reloading calculator config…");
      await loadConfig(true);
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (p) => {
    if (!window.confirm("Delete this rate period?")) return;
    try { await api.delete(`/admin/rates/${p.id}`); toast.success("Deleted"); await loadConfig(true); load(); }
    catch { toast.error("Delete failed"); }
  };

  const doPreview = () => {
    try {
      // temporarily push edited period into cache is complex; preview uses backend
      api.post("/admin/rates/preview", { date: isoDate(new Date()), students: { balvatika: 10, primary: 10, upper_primary: 10, high_school: 10 } })
        .then(({ data }) => setPreview(data));
    } catch { toast.error("Preview failed"); }
  };

  // Keep the raw string while typing so a decimal point isn't stripped mid-entry.
  const DECIMAL_RE = /^\d*\.?\d*$/;
  const setRate = (cat, item, field, val) => {
    if (!DECIMAL_RE.test(val)) return;
    setEdit((e) => {
      const c = { ...e };
      c.rates = { ...c.rates };
      c.rates[cat] = { ...c.rates[cat], items: { ...c.rates[cat].items } };
      c.rates[cat].items[item] = { ...c.rates[cat].items[item], [field]: val };
      return c;
    });
  };
  const setTarget = (cat, val) => {
    if (val !== "" && !DECIMAL_RE.test(val)) return;
    setEdit((e) => ({ ...e, rates: { ...e.rates, [cat]: { ...e.rates[cat], target_total: val === "" ? null : val } } }));
  };

  const setUnitField = (cat, item, val) => {
    setEdit((e) => {
      const c = { ...e };
      c.rates = { ...c.rates };
      c.rates[cat] = { ...c.rates[cat], items: { ...c.rates[cat].items } };
      c.rates[cat].items[item] = { ...c.rates[cat].items[item], unit: val };
      return c;
    });
  };

  const RDEC = /^\d*\.?\d*$/;
  const setIng = (idx, field, val) => {
    if (val !== "" && !RDEC.test(val)) return;
    setRecipe((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };
  const recipeSum = recipe ? recipe.reduce((s, r) => s + (parseFloat(r.rate) || 0), 0) : 0;
  const recipeOk = Math.abs(recipeSum - recipeCost) < 0.001;
  const saveRecipe = async () => {
    if (!recipeOk) { toast.error(`Total per laddoo must equal ₹${recipeCost.toFixed(2)}`); return; }
    setSavingRecipe(true);
    try {
      const payload = { ingredients: recipe.map((r) => ({ ...r, rate: parseFloat(r.rate) || 0, grams: r.grams === null || r.grams === "" ? null : parseFloat(r.grams) })) };
      const { data } = await api.put("/admin/laddoo-recipe", payload);
      setRecipe(data.ingredients);
      toast.success("Laddoo recipe saved. Reloading calculator config…");
      await loadConfig(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingRecipe(false); }
  };

  if (!periods) return <Loader />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold">Rate Periods</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowAudit(!showAudit)} className="btn-ghost text-sm" data-testid="admin-audit-toggle"><HistoryIcon className="w-4 h-4" /> Audit ({audit.length})</button>
          <button onClick={() => openEdit({ effective_from: isoDate(new Date()), effective_to: isoDate(new Date()), name: "New Period", rates: JSON.parse(JSON.stringify(periods.find((p) => p.name?.includes("May 2025"))?.rates || periods[2]?.rates || periods[0].rates)) })} className="btn-3d-green text-sm" data-testid="admin-add-rate"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>

      {showAudit && (
        <div className="card-thali max-h-64 overflow-y-auto space-y-2 text-sm">
          {audit.length ? audit.map((l, i) => (
            <div key={i} className="border-b border-thali-turmeric/10 pb-1"><b>{l.action}</b> by {l.by} · {new Date(l.at).toLocaleString()}</div>
          )) : <div className="text-thali-ink/50">No changes logged.</div>}
        </div>
      )}

      <div className="space-y-2">
        {periods.map((p, i) => (
          <div key={p.id || i} className="card-thali flex items-center justify-between" data-testid={`rate-period-${i}`}>
            <div>
              <div className="font-heading font-bold">{p.name || "Period"}</div>
              <div className="text-sm text-thali-ink/60">{(p.effective_from || "").split("T")[0]} → {(p.effective_to || "").split("T")[0]}</div>
              <div className="text-xs text-thali-ink/50">Target: Bal/Pri {p.rates.balvatika.target_total ?? "—"} · UP/HS {p.rates.upper_primary.target_total ?? "—"}</div>
            </div>
            {p.id && (
              <div className="flex gap-2">
                <button onClick={() => openEdit(p)} className="w-11 h-11 rounded-lg bg-thali-cream grid place-items-center" data-testid={`rate-edit-${i}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p)} className="w-11 h-11 rounded-lg bg-red-50 text-red-600 grid place-items-center" data-testid={`rate-del-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={() => setEdit(null)}>
          <div className="bg-thali-paper rounded-t-card md:rounded-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="rate-editor">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg">Edit Rate Period</h2>
              <button onClick={() => setEdit(null)} className="w-10 h-10 grid place-items-center"><X /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="text-sm font-semibold">Name</label><input className="input-thali mt-1" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="rate-name" /></div>
              <div><label className="text-sm font-semibold">Circular</label><input className="input-thali mt-1" value={edit.circular || ""} onChange={(e) => setEdit({ ...edit, circular: e.target.value })} /></div>
              <div><label className="text-sm font-semibold">From</label><input type="date" className="input-thali mt-1" value={(edit.effective_from || "").split("T")[0]} onChange={(e) => setEdit({ ...edit, effective_from: e.target.value })} data-testid="rate-from" /></div>
              <div><label className="text-sm font-semibold">To</label><input type="date" className="input-thali mt-1" value={(edit.effective_to || "").split("T")[0]} onChange={(e) => setEdit({ ...edit, effective_to: e.target.value })} data-testid="rate-to" /></div>
            </div>
            {CATS.map((cat) => (
              <div key={cat} className="mb-4 rounded-2xl overflow-hidden border border-thali-turmeric/20 shadow-sm" data-testid={`rate-card-${cat}`}>
                <div className={`p-4 text-white bg-gradient-to-r ${CAT_HEAD[cat]}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-heading font-extrabold text-lg flex items-center gap-2">{CAT_EMOJI[cat]} {CAT_LABEL[cat]}</div>
                      <div className="text-white/85 text-xs mt-0.5">Per-child quantity & rate. Auto-balance target is the statutory total for the category.</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] font-bold tracking-wide text-white/90">TARGET TOTAL (₹/CHILD)</div>
                      <input className="mt-1 w-28 rounded-lg bg-white/95 text-thali-ink text-center font-num py-1.5 px-2" inputMode="decimal" placeholder="—" value={edit.rates[cat].target_total ?? ""} onChange={(e) => setTarget(cat, e.target.value)} data-testid={`rate-target-${cat}`} />
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto bg-white">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="bg-thali-cream/60 text-thali-ink/60 text-[11px] font-bold uppercase tracking-wide">
                        <th className="text-left px-4 py-2">Item</th>
                        <th className="text-left px-3 py-2">Qty / Student</th>
                        <th className="text-left px-3 py-2">Unit</th>
                        <th className="text-left px-3 py-2">Rate / Student (₹)</th>
                        <th className="text-left px-3 py-2">Auto-Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(edit.rates[cat].items).map(([item, it]) => (
                        <tr key={item} className="border-t border-thali-turmeric/10">
                          <td className="px-4 py-2 font-semibold whitespace-nowrap">{it.emoji} {item}</td>
                          <td className="px-3 py-2">
                            <input className="w-24 border border-thali-turmeric/30 rounded-lg px-2 py-1.5 font-num" inputMode="decimal" value={it.qty_per_student} onChange={(e) => setRate(cat, item, "qty_per_student", e.target.value)} data-testid={`qty-item-${cat}-${item}`} />
                          </td>
                          <td className="px-3 py-2">
                            <select className="w-24 border border-thali-turmeric/30 rounded-lg px-2 py-1.5 bg-white" value={it.unit || "unit"} onChange={(e) => setUnitField(cat, item, e.target.value)} data-testid={`unit-item-${cat}-${item}`}>
                              {[...new Set([it.unit || "unit", ...UNIT_OPTIONS])].map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input className="w-24 border border-thali-turmeric/30 rounded-lg px-2 py-1.5 font-num" inputMode="decimal" value={it.rate_per_student} onChange={(e) => setRate(cat, item, "rate_per_student", e.target.value)} data-testid={`rate-item-${cat}-${item}`} />
                          </td>
                          <td className="px-3 py-2 text-xs text-thali-ink/50">{it.auto_balance ? "⚖️ Auto (May 2025+)" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            <div className="flex gap-2 sticky bottom-0 bg-thali-paper pt-2">
              <button onClick={save} className="btn-3d-green flex-1" data-testid="rate-save"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="card-thali">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-heading font-bold">Preview (10 each, today)</h2>
          <button onClick={doPreview} className="btn-ghost text-sm" data-testid="rate-preview-btn"><Calculator className="w-4 h-4" /> Run</button>
        </div>
        {preview && <div className="text-sm">Grand total: <b>{formatCurrency(preview.summary.grand_total)}</b> · {preview.summary.total_students} students · {preview.summary.total_rice_kg} kg rice</div>}
      </div>

      {recipe && (
        <div className="card-thali" data-testid="laddoo-recipe-editor">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <h2 className="font-heading font-bold flex items-center gap-2">🧁 Laddoo Recipe</h2>
            <span className={`text-sm font-num font-bold px-3 py-1 rounded-full ${recipeOk ? "bg-thali-green/15 text-thali-green" : "bg-red-100 text-red-600"}`} data-testid="laddoo-recipe-total">
              Total per laddoo = {formatCurrency(recipeSum)} (must equal ₹{recipeCost.toFixed(2)})
            </span>
          </div>
          <p className="text-xs text-thali-ink/60 mb-3">Grams &amp; rate per laddoo (= per student). Saving is blocked unless the total equals the laddoo cost. Future periods with a different cost are auto-scaled.</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="bg-thali-cream/60 text-thali-ink/60 text-[11px] font-bold uppercase tracking-wide">
                  <th className="text-left px-3 py-2">Ingredient</th>
                  <th className="text-left px-3 py-2">Qty / Laddoo (g)</th>
                  <th className="text-left px-3 py-2">Rate / Laddoo (₹)</th>
                </tr>
              </thead>
              <tbody>
                {recipe.map((ing, idx) => (
                  <tr key={ing.key} className="border-t border-thali-turmeric/10">
                    <td className="px-3 py-2 font-semibold whitespace-nowrap">{ing.emoji} {ing.name}</td>
                    <td className="px-3 py-2">
                      {ing.key === "making"
                        ? <span className="text-thali-ink/40">—</span>
                        : <input className="w-24 border border-thali-turmeric/30 rounded-lg px-2 py-1.5 font-num" inputMode="decimal" value={ing.grams ?? ""} onChange={(e) => setIng(idx, "grams", e.target.value)} data-testid={`laddoo-grams-${ing.key}`} />}
                    </td>
                    <td className="px-3 py-2">
                      <input className="w-24 border border-thali-turmeric/30 rounded-lg px-2 py-1.5 font-num" inputMode="decimal" value={ing.rate} onChange={(e) => setIng(idx, "rate", e.target.value)} data-testid={`laddoo-rate-${ing.key}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={saveRecipe} disabled={!recipeOk || savingRecipe} className="btn-3d-green mt-4 disabled:opacity-50" data-testid="laddoo-recipe-save">
            <Save className="w-4 h-4" /> {savingRecipe ? "Saving…" : "Save Recipe"}
          </button>
        </div>
      )}
    </div>
  );
}
